// PG-24 — when is a series allowed to SPEAK for a movement pattern.
//
// The e1RM panel used to hand each pattern to whoever lifted the most kg in
// the window, with no recency and no minimum evidence. On Antenore's own data
// that put `hack-squat-machine` (188,3 kg) over the `back-squat` he actually
// trains (102,1 kg), and a `cable-crunch` from 43 days ago over a
// `dumbbell-side-bend` from 8 — "il peso non è il metro corretto" (2026-09-16).
//
// The fix is not a different weight rule: it is an ADMISSIBILITY rule applied
// before anything competes. A series that is too short, or squeezed into too
// few days, is not a trend — it is a couple of readings, and a couple of
// readings must not own a pattern.
//
// ⛔ Both numbers are DEDUCED from his archive (90d / 42 exercises with an
// e1RM), never declared:
//  - 3 points is the floor a line can be fitted through at all, and it is the
//    number already in production (`detectRegression` returns "plateau" below
//    it, which the row shows as "few data").
//  - 14 days is the knee: with ≥3 points 15 exercises qualify, at 14d span 11
//    remain, and at 21d still 11 — past 14 the filter stops biting, so a wider
//    arc would cost trends without buying trust.
import { DAY_MS } from "./freshness.js";

/**
 * Fewest points a series needs before a line through it means anything.
 * ONE definition: `detectRegression` degrades to "plateau" below it and the
 * trend row shows its "few data" state on the same number, so the threshold
 * that admits a series and the threshold that draws it can never drift apart.
 */
export const MIN_TREND_POINTS = 3;

/**
 * Fewest days a series must SPAN, first point to last. Distinct from the point
 * count and not implied by it: three sets inside one week are three readings of
 * the same day-to-day noise, not a direction.
 */
export const MIN_TREND_SPAN_DAYS = 14;

/**
 * Days from the first point to the last. Fractional — comparing it against a
 * whole-day threshold must not round a 13,5-day arc up into a qualifying one.
 *
 * ⚠️ Precondition: `points` is chronological. Every caller here is fed by the
 * e1RM funnel, which sorts once so no consumer has to (the same assumption
 * `latest` already rides on).
 */
export const trendSpanDays = (
  points: ReadonlyArray<{ atMs: number }>,
): number => {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return 0;
  return (last.atMs - first.atMs) / DAY_MS;
};

/**
 * Is this series allowed to occupy a movement pattern? Enough points AND
 * enough arc — the two are an AND on purpose: either one alone lets through
 * exactly the case the other exists to stop.
 *
 * ⛔ Says nothing about how GOOD the trend is. Admissibility is upstream of
 * the regression verdict: a series can qualify here and still come back a
 * plateau, and that is a real answer, not a failure.
 */
export const meetsTrendThreshold = (
  points: ReadonlyArray<{ atMs: number }>,
): boolean =>
  points.length >= MIN_TREND_POINTS &&
  trendSpanDays(points) >= MIN_TREND_SPAN_DAYS;
